import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaToggleOn, FaToggleOff, FaChevronDown, FaChevronUp } from "react-icons/fa";
import API from "../api";

const emptyService = {
  id: "", title: "", shortDesc: "", fullDesc: "", color: "#0ea5e9", tagline: "",
  image: "", features: [], packages: [], active: true, order: 0,
};

const emptyPackage = { name: "", price: "", features: [] };

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchServices = async () => {
    setLoading(true);
    const { data } = await API.get("/services/all");
    setServices(data);
    setLoading(false);
  };

  useEffect(() => { fetchServices(); }, []);

  const handleSave = async () => {
    try {
      if (modal.mode === "add") {
        const { data } = await API.post("/services", modal.data);
        setServices((prev) => [...prev, data]);
      } else {
        const { data } = await API.put(`/services/${modal.data._id}`, modal.data);
        setServices((prev) => prev.map((s) => s._id === data._id ? data : s));
      }
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await API.delete(`/services/${id}`);
    setServices((prev) => prev.filter((s) => s._id !== id));
  };

  const toggleActive = async (service) => {
    const { data } = await API.put(`/services/${service._id}`, { ...service, active: !service.active });
    setServices((prev) => prev.map((s) => s._id === data._id ? data : s));
  };

  const update = (key, value) => setModal((prev) => ({ ...prev, data: { ...prev.data, [key]: value } }));

  const updatePackage = (i, key, value) => {
    const pkgs = [...(modal.data.packages || [])];
    pkgs[i] = { ...pkgs[i], [key]: value };
    update("packages", pkgs);
  };

  const addPackage = () => update("packages", [...(modal.data.packages || []), { ...emptyPackage }]);
  const removePackage = (i) => update("packages", modal.data.packages.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">Services</h1>
          <p className="text-gray-400 text-sm">{services.length} services</p>
        </div>
        <motion.button onClick={() => setModal({ mode: "add", data: { ...emptyService } })}
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          className="btn-primary px-4 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center gap-2">
          <FaPlus className="text-xs" /> <span>Add Service</span>
        </motion.button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <motion.div key={service._id} layout className={`glass rounded-2xl border border-white/10 overflow-hidden ${!service.active ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                  style={{ background: `${service.color}20`, color: service.color }}>
                  {service.id?.[0]?.toUpperCase() || "S"}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-sm">{service.title}</h3>
                  <p className="text-gray-400 text-xs truncate">{service.shortDesc}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleActive(service)} className="text-lg">
                    {service.active ? <FaToggleOn className="text-green-400" /> : <FaToggleOff className="text-gray-500" />}
                  </button>
                  <button onClick={() => setModal({ mode: "edit", data: { ...service } })}
                    className="w-8 h-8 glass rounded-lg flex items-center justify-center text-blue-400 hover:text-blue-300 text-xs">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(service._id)}
                    className="w-8 h-8 glass rounded-lg flex items-center justify-center text-red-400 hover:text-red-300 text-xs">
                    <FaTrash />
                  </button>
                  <button onClick={() => setExpanded(expanded === service._id ? null : service._id)}
                    className="w-8 h-8 glass rounded-lg flex items-center justify-center text-gray-400 hover:text-white text-xs">
                    {expanded === service._id ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
              </div>
              <AnimatePresence>
                {expanded === service._id && (
                  <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
                    className="overflow-hidden border-t border-white/10">
                    <div className="p-4 grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500 text-xs mb-2">Features</p>
                        <ul className="space-y-1">
                          {(service.features || []).map((f) => (
                            <li key={f} className="text-gray-300 text-xs flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: service.color }} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs mb-2">Packages</p>
                        <div className="space-y-2">
                          {(service.packages || []).map((pkg) => (
                            <div key={pkg.name} className="glass rounded-xl p-3">
                              <div className="flex justify-between">
                                <span className="text-white text-xs font-semibold">{pkg.name}</span>
                                <span className="text-xs font-bold" style={{ color: service.color }}>{pkg.price}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setModal(null)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass rounded-2xl p-6 border border-white/10"
              style={{ background: "rgba(10,15,30,0.97)" }}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-bold text-lg">{modal.mode === "add" ? "Add" : "Edit"} Service</h2>
                <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><FaTimes /></button>
              </div>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { key: "id", label: "Service ID", placeholder: "web" },
                    { key: "title", label: "Title", placeholder: "Web Development" },
                    { key: "tagline", label: "Tagline", placeholder: "React.js + Node.js Powered" },
                    { key: "image", label: "Image URL", placeholder: "/images/web-dev.jpeg" },
                    { key: "order", label: "Order", type: "number" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="text-gray-400 text-xs mb-1.5 block">{f.label}</label>
                      <input type={f.type || "text"} value={modal.data[f.key] || ""}
                        onChange={(e) => update(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Accent Color</label>
                  <div className="flex gap-3 items-center">
                    <input type="color" value={modal.data.color || "#0ea5e9"}
                      onChange={(e) => update("color", e.target.value)}
                      className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent" />
                    <input type="text" value={modal.data.color || ""}
                      onChange={(e) => update("color", e.target.value)}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                </div>

                {[
                  { key: "shortDesc", label: "Short Description" },
                  { key: "fullDesc", label: "Full Description" },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-gray-400 text-xs mb-1.5 block">{f.label}</label>
                    <textarea rows={3} value={modal.data[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm resize-none" />
                  </div>
                ))}

                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block">Features (one per line)</label>
                  <textarea rows={4} value={(modal.data.features || []).join("\n")}
                    onChange={(e) => update("features", e.target.value.split("\n").filter(Boolean))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm resize-none" />
                </div>

                {/* Packages */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-400 text-xs">Packages</label>
                    <button onClick={addPackage} className="text-blue-400 text-xs hover:text-blue-300 flex items-center gap-1">
                      <FaPlus className="text-xs" /> Add Package
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(modal.data.packages || []).map((pkg, i) => (
                      <div key={i} className="glass rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between mb-3">
                          <span className="text-white text-xs font-semibold">Package {i + 1}</span>
                          <button onClick={() => removePackage(i)} className="text-red-400 hover:text-red-300 text-xs"><FaTrash /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <input type="text" value={pkg.name || ""} onChange={(e) => updatePackage(i, "name", e.target.value)}
                            placeholder="Package Name"
                            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500" />
                          <input type="text" value={pkg.price || ""} onChange={(e) => updatePackage(i, "price", e.target.value)}
                            placeholder="₹24,999"
                            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500" />
                        </div>
                        <textarea rows={2} value={(pkg.features || []).join("\n")}
                          onChange={(e) => updatePackage(i, "features", e.target.value.split("\n").filter(Boolean))}
                          placeholder="Features (one per line)"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500 resize-none" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setModal(null)}
                  className="flex-1 py-2.5 glass rounded-xl text-gray-400 hover:text-white text-sm font-semibold">
                  Cancel
                </button>
                <motion.button onClick={handleSave} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex-1 btn-primary py-2.5 rounded-xl text-white text-sm font-semibold">
                  <span>Save Service</span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
